import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, inject, Input, NgZone, PLATFORM_ID, QueryList, ViewChildren } from '@angular/core';

@Component({
  selector: 'app-stars-background',
  imports: [],
  templateUrl: './stars-background.component.html',
  styleUrl: './stars-background.component.less'
})
export class StarsBackgroundComponent implements AfterViewInit {
  @ViewChildren('starCanvas') starCanvas!: QueryList<ElementRef>;

  @Input() numberOfStars: number = 300;

  private ngZone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);
  
  private readonly STAR_SPEED = 150;

  shouldCreateNewShootingStar = true;
  private requestAnimationFrameId: number | null = null;
  private timeoutId: number | null = null;
  private continueAnimating = true;

  stars: { x: number, y: number, size: number, opacity: number, targetSize: number }[] = [];
  shootingStar: { x: number, y: number, dx: number, dy: number, length: number, duration: number, elapsed: number, opacity: number } = {  x: 0, y: 0, dx: 0, dy: 0, length: 0, duration: 0, elapsed: 0, opacity: 0 };

  constructor() { }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.starCanvas.forEach(starCanvasRef => this.setupCanvas(starCanvasRef.nativeElement));
    }
  }

  setupCanvas(canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d');
    if (!context) {
      console.error('Failed to get 2d context');
      return;
    }

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    // Ejecutamos toda la lógica de animación FUERA de la zona de Angular desde el principio
    this.ngZone.runOutsideAngular(() => {
      this.initializeStars(canvas, this.numberOfStars);
      this.generateNewShootingStar();
      this.animateStars(canvas, context);
    });
  }

  initializeStars(canvas: HTMLCanvasElement, numberOfStars: number) {
    for (let i = 0; i < numberOfStars; i++) {
      this.stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 2,
        opacity: Math.random(),
        targetSize: Math.random() * 2
      });
    }
  }

  generateNewShootingStar() {
    this.clearTimeout();
    if (!this.continueAnimating) return;
    const angle = Math.random() * 2 * Math.PI;
    const duration = Math.random() * 3000 + 3000;

    this.shootingStar = {
      x: Math.random() * 800,
      y: Math.random() * 600,
      dx: Math.cos(angle) * this.STAR_SPEED,
      dy: Math.sin(angle) * this.STAR_SPEED,
      length: Math.random() * 100 + 50,
      duration: duration,
      elapsed: 0,
      opacity: 1
    };
    
    if (this.shouldCreateNewShootingStar) {
      // Ya estamos fuera de Angular (iniciado en setupCanvas), no necesitamos volver a llamar a runOutsideAngular
      // ni mucho menos a ngZone.run()
      this.timeoutId = window.setTimeout(() => {
          this.generateNewShootingStar();
      }, this.shootingStar.duration + Math.random() * 4000);
    }
  }

  updateStars() {
    for (const star of this.stars) {
      if (Math.random() < 0.02) {
        star.targetSize = Math.random() < 0.5 ? 0 : (Math.random() * 2 + 1);
      }
      star.size += (star.targetSize - star.size) * 0.02;
    }
  }

  updateShootingStar(deltaTime: number) {
    this.shootingStar.x += this.shootingStar.dx * (deltaTime / 1000);
    this.shootingStar.y += this.shootingStar.dy * (deltaTime / 1000);
    this.shootingStar.elapsed += deltaTime;
    this.shootingStar.opacity = 1 - (this.shootingStar.elapsed / this.shootingStar.duration);
  }

  animateStars(canvas: HTMLCanvasElement, context: CanvasRenderingContext2D) {
    // Importante: No envolvemos esto en ngZone.run()
    this.cancelAnimationFrame();
    context.clearRect(0, 0, canvas.width, canvas.height);

    this.updateStars();

    for (const star of this.stars) {
      context.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
      context.fillRect(star.x, star.y, star.size, star.size);
    }

    if (this.shootingStar.elapsed <= this.shootingStar.duration) {
      const gradient = context.createLinearGradient(
        this.shootingStar.x,
        this.shootingStar.y,
        this.shootingStar.x + this.shootingStar.dx * (this.shootingStar.length / 200),
        this.shootingStar.y + this.shootingStar.dy * (this.shootingStar.length / 200)
      );
      gradient.addColorStop(0, `rgba(255,255,255,${this.shootingStar.opacity * 0.0})`);
      gradient.addColorStop(1, `rgba(255,255,255,${this.shootingStar.opacity * 1.0})`);

      context.strokeStyle = gradient;
      context.lineWidth = 2;
      context.beginPath();
      context.moveTo(this.shootingStar.x, this.shootingStar.y);
      context.lineTo(this.shootingStar.x + this.shootingStar.dx * (this.shootingStar.length / 200),
        this.shootingStar.y + this.shootingStar.dy * (this.shootingStar.length / 200));
      context.stroke();

      this.updateShootingStar(16);
    }

    // Solicitamos el siguiente frame sin reingresar a la zona de Angular
    this.requestAnimationFrameId = requestAnimationFrame(() => {
        this.animateStars(canvas, context);
    });
  }

  public redrawCanvas() {
    if (isPlatformBrowser(this.platformId)) {
      this.clearTimeout();
      this.cancelAnimationFrame();
      this.stars = [];
      // setupCanvas volverá a llamar a runOutsideAngular, manteniendo el ciclo limpio
      this.starCanvas.forEach(starCanvasRef => this.setupCanvas(starCanvasRef.nativeElement));
    }
  }

  clearTimeout() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
  
  cancelAnimationFrame() {
    if (this.requestAnimationFrameId) {
      cancelAnimationFrame(this.requestAnimationFrameId);
      this.requestAnimationFrameId = null;
    }
  }

  public stopAnimation() {
    this.continueAnimating = false;
    this.clearTimeout();
    this.cancelAnimationFrame();
  }

  @HostListener('window:resize')
  onResize() {
    if (isPlatformBrowser(this.platformId)) {
      this.redrawCanvas();
    }
  }
}